import axios from 'axios';
import i18n from 'i18next';
import images from 'images';
import { dynamicObjectType } from 'types';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import useAPIStore from '../APIStore';
import useUserStore from '../UserStore';

interface GPSProps {
	lat: number;
	lng: number;
}

interface sysInfoProps {
	equipmentPlatform: string;
	equipmentOS: string;
	equipmentBrowser: string;
	equipmentModel: string;
	isAppSupported: boolean;
}

interface cameraCheckResultProps {
	maxPreviewWidth: number;
	maxPreviewHeight: number;
}

declare class ARDetector {
	constructor(_requiredArgs: object, _optionalArgs: object);
	getVersion(): string;
	init(): Promise<any>;
	checkSystemCapability(): void;
	checkCameraCapability(): Promise<any>;
	getSystemInformation(): sysInfoProps;
	getCameraCheckResult(): cameraCheckResultProps;
	getAuthMode(): number;
	stopARCodeDetector(): void;
	getTorchAvailability(): boolean;
	QRReaderHandler(
		parent: HTMLElement | null,
		torch?: HTMLElement | null
	): Promise<any>;
	guidedModeHandler(
		parent: HTMLElement | null,
		torch?: HTMLElement | null
	): Promise<any>;
	manualQRReaderHandler(): Promise<any>;
	manualPhotoModeHandler(): Promise<any>;
	setLanguage(lang: string): void;
	findOwnerShipCardQR(): Promise<string>;
}

interface SystemStates {
	sdkMode: string;
	gps: GPSProps | undefined;
	gpsString: string | undefined;
	sdk: ARDetector | undefined;
	wxsdk: any | undefined;
	sdkLoadingPercentage: number;
	sysInfo: sysInfoProps | undefined;
	cameraInfo: cameraCheckResultProps | undefined;
	authMode: number | undefined;
	torchAvailability: boolean;
}

interface SystemActions {
	actions: {
		isWeChat: (envOnly?: boolean) => boolean;
		resolveMultiLang: (obj: dynamicObjectType) => string | null;
		getCurrentPosition: () => Promise<void>;
		getImage: (imageName: string, language?: string) => string;
		initializeWXSDK: () => void;
		initializeSDK: () => void;
		setSDKMode: (mode: string | null) => void;
		setAuthMode: (mode: number) => void;
		setTorchAvailability: (torch: boolean) => void;
		setCameraInfo: (info: cameraCheckResultProps) => void;
	};
}

const awaitSDK = () => {
	return new Promise<void>((resolve) => {
		const sdkChecker = window.setInterval(() => {
			try {
				new ARDetector({}, {});
				window.clearInterval(sdkChecker);
				resolve();
			} catch (e) {}
		}, 20);
	});
};

const useSystemStore = create<SystemStates & SystemActions>((set, get) => ({
	sdkMode: 'latest',
	gps: undefined,
	gpsString: undefined,
	sdk: undefined,
	wxsdk: undefined,
	sdkLoadingPercentage: 0,
	sysInfo: undefined,
	cameraInfo: undefined,
	authMode: undefined,
	torchAvailability: false,
	actions: {
		isWeChat: (envOnly = false) => {
			const env = /MicroMessenger/i.test(window.navigator.userAgent);
			const isWeChatUser = useUserStore.getState().isWeChatUser;
			if (!envOnly && !isWeChatUser) return false;
			return env;
		},
		resolveMultiLang: (obj) => {
			if (!obj) return null;
			if (obj[i18n.resolvedLanguage]) return obj[i18n.resolvedLanguage];
			return obj[process.env.REACT_APP_DEFAULT_LANG!];
		},
		getCurrentPosition: () => {
			return new Promise<void>((resolve) => {
				if (!navigator.geolocation) {
					// console.log("geolocation is not available")
					resolve();
				}
				navigator.geolocation.getCurrentPosition(
					(geoPosition) => {
						set(() => ({
							gps: {
								lat: geoPosition.coords.latitude,
								lng: geoPosition.coords.longitude,
							},
							gpsString: `${geoPosition.coords.longitude},${geoPosition.coords.latitude}`,
						}));
						resolve();
					},
					(geoError) => {
						// console.log("get geolocation error")
						// console.log(geoError)
						resolve();
					},
					{ timeout: 3000 }
				);
			});
		},
		getImage: (imageName, language) => {
			if (!images.hasOwnProperty(imageName)) return;
			const image = images[imageName];
			if (image.hasOwnProperty('common')) return image['common'];
			if (language && image.hasOwnProperty(language))
				return image[language];
			if (image.hasOwnProperty(i18n.resolvedLanguage))
				return image[i18n.resolvedLanguage];
			return image[process.env.REACT_APP_DEFAULT_LANG!];
		},
		initializeWXSDK: () => {
			if (document.getElementById('wechat_js_sdk')) {
				return;
			}

			const script = document.createElement('script');
			document.body.appendChild(script);
			script.type = 'text/javascript';
			script.async = true;
			script.id = 'wechat_js_sdk';

			axios
				.get(`https://res.wx.qq.com/open/js/jweixin-1.6.0.js`, {
					responseType: 'blob',
				})
				.then((response) => {
					script.src = URL.createObjectURL(response.data);
					script.onload = async () => {
						useAPIStore
							.getState()
							.api.obtainWXJSConfig()
							.then((res) => {
								//	@ts-ignore
								const wx = window.wx;
								wx.config({
									debug: res.data.isDebug,
									appId: res.data.appId,
									timestamp: res.data.timestamp,
									nonceStr: res.data.nonceStr,
									signature: res.data.signature,
									jsApiList: res.data.jsApiList,
								});
								wx.ready(() => {
									set(() => ({
										wxsdk: wx,
									}));
									console.log('wx sdk is ready');
								});
							});
					};
				})
				.catch((error) => {
					console.log(error);
				});
		},
		initializeSDK: () => {
			if (document.getElementById('arcode_webscan_oss_sdk')) {
				return;
			}

			const script = document.createElement('script');
			document.body.appendChild(script);
			script.type = 'text/javascript';
			script.async = true;
			script.id = 'arcode_webscan_oss_sdk';
			const mode = get().sdkMode;

			axios
				.get(
					`https://arcode-websdk.oss-accelerate.aliyuncs.com/ARCodeSDK_${mode}.min.js`,
					{
						responseType: 'blob',
						onDownloadProgress: (progressEvent) => {
							set(() => ({
								sdkLoadingPercentage: Math.floor(
									(progressEvent.loaded /
										progressEvent.event.currentTarget.getResponseHeader(
											'x-oss-meta-content-length'
										)) *
										100
								),
							}));
						},
					}
				)
				.then((response) => {
					script.src = URL.createObjectURL(response.data);
					script.onload = async () => {
						await awaitSDK();
						// console.log('SDK loaded');
						let ARSDK = new ARDetector(
							{},
							{
								disableManualPhotoMode: false,
								disableGuidedPhotoMode: false,
								timeout: 20000,
								language: i18n.resolvedLanguage.replace(
									'_',
									'-'
								),
								location: get().gps,
								token: useUserStore.getState().actions
									.acquireToken,
								//	Old API
								// ownerScan: true,
								// getUaidInfoAPI: `${process.env.REACT_APP_DOMAIN_NAME}/apiUaid/uaid/getBasicUaidInfoByQrCode`,
								// verifyAPI: `${process.env.REACT_APP_DOMAIN_NAME}/apiVerify/verify/web/webVerify`,
								//	Open API
								useOpenAPI: true,
								openAPIUAIDInfo: `${process.env.REACT_APP_DOMAIN_NAME}${process.env.REACT_APP_OPENAPI_UAID_INFO_API}`,
								openAPIUAIDAuth: `${process.env.REACT_APP_DOMAIN_NAME}${process.env.REACT_APP_OPENAPI_UAID_AUTH_API}`,
								openAPIApplicationType: () =>
									get().actions.isWeChat() ? '3' : '2',
								checkHiddenCode: true,
								defaultTorchOn:
									process.env.REACT_APP_DEFAULT_TORCH_ON ===
									'true',
								thirdPartyCode:
									process.env
										.REACT_APP_THIRD_PARTY_CODE_ONLY ===
									'true',
								quitOnSSIMAuthFail:
									process.env
										.REACT_APP_QUIT_ON_SSIM_AUTH_FAIL ===
									'true',
								showAuthErrorOnTimeout:
									process.env
										.REACT_APP_SHOW_AUTH_ERROR_ON_TIMEOUT ===
									'true',
							}
						);
						// console.log(ARSDK.getVersion());
						await ARSDK.init();
						set(() => ({
							sysInfo: ARSDK.getSystemInformation(),
							sdk: ARSDK,
						}));
						ARSDK.checkSystemCapability();
					};
				})
				.catch((error) => {
					console.log(error);
				});
		},
		setSDKMode: (mode) => {
			mode &&
				set(() => ({
					sdkMode: mode,
				}));
		},
		setAuthMode: (mode) => {
			set(() => ({
				authMode: mode,
			}));
		},
		setTorchAvailability: (torch) => {
			set(() => ({
				torchAvailability: torch,
			}));
		},
		setCameraInfo: (info) => {
			set(() => ({
				cameraInfo: info,
			}));
		},
	},
}));

export const useGPSString = () => useSystemStore((state) => state.gpsString);
export const useSDKLoadingPercentage = () =>
	useSystemStore((state) => state.sdkLoadingPercentage);
export const useSDK = () =>
	useSystemStore(
		(state) => ({
			sdk: state.sdk,
			authMode: state.authMode,
			torchAvailability: state.torchAvailability,
			sysInfo: state.sysInfo,
			cameraInfo: state.cameraInfo,
		}),
		shallow
	);
export const useWXSDK = () => useSystemStore((state) => state.wxsdk);
export const useSystemActions = () => useSystemStore((state) => state.actions);

export default useSystemStore;
